import type {
  IssueFieldSelection,
  IssueFieldSelectionValues,
  IssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import {getIssueFieldValue, toIssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import type {IssueFieldsUsageEvent} from '@github-ui/issue-fields-shared/issue-fields-analytics'
import {useIssueFieldsAnalytics} from '@github-ui/issue-fields-shared/use-issue-fields-analytics'
import {noop} from '@github-ui/noop'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Tooltip} from '@primer/react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useFragment, useRelayEnvironment} from 'react-relay'
import {graphql} from 'relay-runtime'

import {
  areFieldValuesEqual,
  createClearedFieldValue,
  getFieldChanges,
  getIssueFieldSelectionValue,
  type IssueFieldWithUnsavedValue,
  toIssueFieldWithUnsavedValue,
  toIssueFieldWithValue,
} from '../../utils/issue-field-helper'
import type {FieldsSectionFieldValues$key, IssueFieldDataType} from './__generated__/FieldsSectionFieldValues.graphql'
import type {FieldsSectionFragment$key} from './__generated__/FieldsSectionFragment.graphql'
import {AddIssueFieldButton} from './AddIssueFieldButton'
import {LABELS} from './constants/labels'
import {FieldsSelector} from './FieldSelector'
import {ErrorDialog, getEditor, getIssueFieldEditor, getPendingEditor, type SafeFieldValue} from './IssueFieldEditors'
import {commitCreateIssueFieldValueMutation} from './mutations/create-issue-field-value-mutation'
import {commitDeleteIssueFieldValueMutation} from './mutations/delete-issue-field-value-mutation'
import {commitUpdateIssueFieldValueMutation} from './mutations/update-issue-field-value-mutation'

export type CreateIssueFieldsSectionProps = {
  owner: string
  onSelectionChange: (fields: IssueFieldWithUnsavedValue[]) => void
  shortcutEnabled: boolean
}

// helper for mutation analytics and error handling
type MutationAction = 'create' | 'update' | 'delete'

type Mutation<TInput> = (args: {
  environment: ReturnType<typeof useRelayEnvironment>
  input: TInput
  onCompleted: () => void
  onError: (error: unknown) => void
}) => void

function runFieldMutation<TInput>({
  mutationFn,
  input,
  environment,
  action,
  fieldId,
  fieldType,
  inProjectsPage,
  reportUsage,
  onError,
}: {
  mutationFn: Mutation<TInput>
  input: TInput
  environment: ReturnType<typeof useRelayEnvironment>
  action: MutationAction
  fieldId: string
  fieldType: IssueFieldType
  inProjectsPage?: boolean
  reportUsage: (event: IssueFieldsUsageEvent) => void
  onError?: (error: unknown) => void
}) {
  const startTime = Date.now()
  mutationFn({
    environment,
    input,
    onCompleted: () => {
      const duration = Date.now() - startTime
      reportUsage({
        page: inProjectsPage ? 'memex#show' : 'issue#show',
        action,
        fieldId,
        fieldType,
        status: 'success',
        duration,
      })
    },
    onError: error => {
      const duration = Date.now() - startTime
      reportUsage({
        page: inProjectsPage ? 'memex#show' : 'issue#show',
        action,
        fieldId,
        fieldType,
        status: 'error',
        errorDetails: error instanceof Error ? error.message : String(error),
        duration,
      })
      onError?.(error)
    },
  })
}

export function CreateIssueFieldsSection({owner, onSelectionChange, shortcutEnabled}: CreateIssueFieldsSectionProps) {
  const [fields, setFields] = useState<IssueFieldWithUnsavedValue[]>([])
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [pendingField, setPendingField] = useState<IssueFieldSelection | null>(null)
  const {markInteractionStart, reportUsage} = useIssueFieldsAnalytics()

  useEffect(() => {
    onSelectionChange(fields)
  }, [fields, onSelectionChange])

  const onIssueFieldSelected = useCallback(
    (selectedField: IssueFieldSelection) => {
      markInteractionStart()

      if (!selectedField.id) return
      setPendingField(selectedField)

      if (selectedField.name) {
        setFieldsSet(prev => [...prev, selectedField.name])
      }
    },
    [markInteractionStart],
  )

  const onCommit = useCallback(
    (fieldId: string, value: IssueFieldSelectionValues) => {
      setFields(prevFields => {
        let committedField: IssueFieldWithUnsavedValue | undefined
        const updatedFields = prevFields
          // update new value
          .map(field => {
            if (field.field.id === fieldId) {
              committedField = toIssueFieldWithValue(field.field, value)
              return committedField
            }
            return field
          })
          // filter out empty values
          .filter(f => f.value)

        // remove from fieldsSet if field is being cleared
        if (committedField && !committedField.value) {
          setFieldsSet(prev => prev.filter(name => name !== committedField?.field.name))
        }

        if (committedField && committedField.field.dataType) {
          reportUsage({
            page: 'issue#new',
            action: 'create',
            fieldId,
            fieldType: toIssueFieldType(committedField.field.dataType),
            status: 'success',
          })
        }

        return updatedFields
      })
    },
    [reportUsage],
  )

  const onPendingFieldCommit = useCallback(
    (fieldId: string, fieldValue: IssueFieldSelectionValues) => {
      if (!pendingField || pendingField.id !== fieldId) return

      // Remove from pending state
      setPendingField(null)

      const fieldType = toIssueFieldType(pendingField.dataType)
      const value = getIssueFieldSelectionValue(fieldType, fieldValue)
      const isEmpty = typeof value === 'string' ? !value.trim() : !value

      // Remove from fieldsSet if field is being cancelled/removed
      if (pendingField.name && isEmpty) {
        setFieldsSet(prev => prev.filter(name => name !== pendingField.name))
        return
      }

      // If value is empty, don't save the value (field is being removed)
      if (isEmpty) {
        return
      }

      // Commit the field value
      setFields(prevFields => {
        const newFields = [...prevFields]
        if (pendingField.name && !newFields.some(f => f.field.name === pendingField.name)) {
          newFields.push(toIssueFieldWithUnsavedValue(pendingField, fieldValue))
        }
        return newFields
      })
    },
    [pendingField],
  )

  return (
    <>
      {fields.map((field, index) => {
        if (!field.field.id) return null
        const isLastItem = index === fields.length - 1

        const fieldValue = typeof field.value === 'object' && field.value !== null ? {...field, ...field.value} : field
        return getEditor({
          fieldValue: fieldValue as SafeFieldValue,
          readonly: false,
          hideDivider: isLastItem,
          onCommit,
        })
      })}
      {pendingField &&
        getPendingEditor({
          fieldSelection: pendingField,
          readonly: false,
          hideDivider: false,
          onCommit: onPendingFieldCommit,
        })}
      <FieldsSelector
        owner={owner}
        fieldsSet={fieldsSet}
        onIssueFieldSelected={onIssueFieldSelected}
        shortcutEnabled={shortcutEnabled}
        markInteractionStart={markInteractionStart}
        disabled={pendingField !== null}
      />
    </>
  )
}

export type EditIssueFieldsSectionProps = {
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  issue: FieldsSectionFragment$key
  insideSidePanel?: boolean
  inProjectsPage?: boolean
}

export function EditIssueFieldsSection({
  issue,
  singleKeyShortcutsEnabled,
  inProjectsPage,
}: EditIssueFieldsSectionProps) {
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [mutationErrorPresent, setMutationErrorPresent] = useState<boolean>(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false)
  const [pendingField, setPendingField] = useState<IssueFieldSelection | null>(null)
  const environment = useRelayEnvironment()
  const {markInteractionStart, reportUsage} = useIssueFieldsAnalytics()

  const data = useFragment(
    graphql`
      fragment FieldsSectionFragment on Issue {
        id
        repository {
          owner {
            login
          }
        }
        viewerCanSetFields
        ...FieldsSectionFieldValues
      }
    `,
    issue,
  )
  const {
    repository: {
      owner: {login: owner},
    },
    viewerCanSetFields,
  } = data

  const handleErrors = useCallback(() => {
    setMutationErrorPresent(true)
  }, [])

  const onCommitPendingField = useCallback(
    (fieldId: string, payload: string | null) => {
      if (!pendingField || pendingField.id !== fieldId) return

      // Remove from pending state
      setPendingField(null)

      const value = getIssueFieldSelectionValue(pendingField.dataType, payload || '')
      const isEmpty = typeof value === 'string' ? !value.trim() : !value

      // Remove from fieldsSet if field is being cancelled/removed
      if (pendingField.name && isEmpty) {
        setFieldsSet(prev => prev.filter(name => name !== pendingField.name))
        return
      }

      // If value is empty, don't make API call (field is being removed)
      if (isEmpty) {
        return
      }

      // Make API call to create the field with the committed value
      runFieldMutation({
        mutationFn: commitCreateIssueFieldValueMutation,
        input: {
          issueId: data.id,
          issueField: {
            fieldId,
            ...getIssueFieldValue(pendingField.dataType, value),
          },
        },
        environment,
        action: 'create',
        fieldId,
        fieldType: toIssueFieldType(pendingField.dataType),
        inProjectsPage,
        reportUsage,
        onError: handleErrors,
      })
    },
    [environment, data.id, handleErrors, reportUsage, inProjectsPage, pendingField],
  )

  const onIssueFieldSelected = (selectedField: IssueFieldSelection) => {
    if (!selectedField.id) return
    setPendingField(selectedField)

    // Add to fieldsSet to prevent it from showing in selector again
    if (selectedField.name) {
      setFieldsSet(prev => [...prev, selectedField.name])
    }
  }

  return (
    <>
      <ErrorBoundary
        fallback={
          <div>
            <Tooltip text={LABELS.loadingFieldsTooltipMessage}>
              <span>
                <AddIssueFieldButton inactive anchorProps={{onClick: () => setErrorDialogOpen(true)}} onClick={noop} />
              </span>
            </Tooltip>
            {errorDialogOpen && (
              <ErrorDialog onClose={() => setErrorDialogOpen(false)} message={LABELS.loadingFieldsErrorMessage} />
            )}
          </div>
        }
      >
        <IssueFields
          issue={data}
          fieldsSet={fieldsSet}
          setFieldsSet={setFieldsSet}
          inProjectsPage={inProjectsPage}
          hasPendingFields={pendingField !== null}
        />

        {/* Render pending field */}
        {pendingField &&
          getPendingEditor({
            fieldSelection: pendingField,
            readonly: false,
            hideDivider: false,
            onCommit: onCommitPendingField,
          })}

        {viewerCanSetFields && (
          <FieldsSelector
            owner={owner}
            fieldsSet={fieldsSet}
            onIssueFieldSelected={onIssueFieldSelected}
            shortcutEnabled={singleKeyShortcutsEnabled}
            markInteractionStart={markInteractionStart}
            disabled={pendingField !== null}
          />
        )}
      </ErrorBoundary>

      {mutationErrorPresent && (
        <ErrorDialog onClose={() => setMutationErrorPresent(false)} message={LABELS.mutationErrorMessage} />
      )}
    </>
  )
}

function IssueFields({
  issue,
  fieldsSet,
  setFieldsSet,
  inProjectsPage,
  hasPendingFields = false,
}: {
  issue: FieldsSectionFieldValues$key
  fieldsSet: string[]
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
  inProjectsPage?: boolean
  hasPendingFields?: boolean
}) {
  const environment = useRelayEnvironment()
  const [errorPresent, setErrorPresent] = useState<boolean>(false)
  const {reportUsage} = useIssueFieldsAnalytics()
  const data = useFragment(
    graphql`
      fragment FieldsSectionFieldValues on Issue {
        id
        issueFieldValues(first: 25) {
          nodes {
            ... on IssueFieldTextValue {
              field {
                ... on IssueFieldText {
                  id
                  name
                  dataType
                }
              }
              value
            }
            ... on IssueFieldSingleSelectValue {
              field {
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                }
              }
              name
              # eslint-disable-next-line relay/unused-fields // this is used in IssueFieldEditors.tsx
              color
              # eslint-disable-next-line relay/unused-fields // this is used in IssueFieldEditors.tsx
              description
            }
            ... on IssueFieldDateValue {
              field {
                ... on IssueFieldDate {
                  id
                  name
                  dataType
                }
              }
              value
            }
            ... on IssueFieldNumberValue {
              field {
                ... on IssueFieldNumber {
                  id
                  name
                  dataType
                }
              }
              # eslint-disable-next-line relay/unused-fields
              numberValue: value
            }
          }
        }
        viewerCanSetFields
      }
    `,
    issue,
  )

  const filteredFieldValues: SafeFieldValue[] = (data.issueFieldValues?.nodes || []).map(fv => fv as SafeFieldValue)
  const filteredFieldsNames = filteredFieldValues.map(fv => fv?.field?.name || '')

  const [effectiveFieldValues, setEffectiveFieldValues] = useState<SafeFieldValue[]>(
    filteredFieldValues.filter(fv => fv?.field != null),
  )

  const handleErrors = useCallback(() => {
    setErrorPresent(true)
  }, [])
  useEffect(() => {
    if (fieldsSet.join(',') !== filteredFieldsNames.join(',')) {
      setFieldsSet(filteredFieldsNames)
    }
  }, [fieldsSet, filteredFieldsNames, setFieldsSet])

  const onCommitField = useCallback(
    (fieldId: string, fieldType: IssueFieldDataType, value: IssueFieldSelectionValues) => {
      const issueFieldType = toIssueFieldType(fieldType)
      const selectionValue = getIssueFieldSelectionValue(issueFieldType, value)
      if (value === '' || value === null) {
        runFieldMutation({
          mutationFn: commitDeleteIssueFieldValueMutation,
          input: {
            issueId: data.id,
            fieldId,
          },
          environment,
          action: 'delete',
          fieldId,
          fieldType: issueFieldType,
          inProjectsPage,
          reportUsage,
          onError: handleErrors,
        })
      } else {
        runFieldMutation({
          mutationFn: commitUpdateIssueFieldValueMutation,
          input: {
            issueId: data.id,
            issueField: {
              fieldId,
              ...getIssueFieldValue(issueFieldType, selectionValue),
            },
          },
          environment,
          action: 'update',
          fieldId,
          fieldType: issueFieldType,
          inProjectsPage,
          reportUsage,
          onError: handleErrors,
        })
      }
    },
    [data.id, environment, handleErrors, inProjectsPage, reportUsage],
  )

  useMemo(() => {
    const {addedFields, updatedFields, removedFields} = getFieldChanges(effectiveFieldValues, filteredFieldValues)

    if (addedFields.length === 0 && updatedFields.length === 0 && removedFields.length === 0) {
      return
    }

    let shouldSet = addedFields.length > 0

    // We want to maintain the initial order of fields, so we iterate over effectiveFieldValues
    const updatedFieldValues = effectiveFieldValues
      .map(fieldValue => {
        const updatedField = updatedFields.find(uf => uf?.field?.id === fieldValue?.field?.id)
        if (updatedField) {
          // eslint-disable-next-line react-hooks/react-compiler
          if (!areFieldValuesEqual(updatedField, fieldValue)) shouldSet = true
          return updatedField
        }

        // Handle field removals: remove text fields, clear value for single-select and date
        const isRemoved = removedFields.some(
          (removedField: (typeof filteredFieldValues)[number]) => removedField?.field?.id === fieldValue?.field?.id,
        )
        if (isRemoved) {
          const clearedField = createClearedFieldValue(fieldValue)
          if (!areFieldValuesEqual(clearedField, fieldValue)) shouldSet = true
          return clearedField
        }

        return fieldValue
      })
      .filter(fv => fv !== null && fv !== undefined)

    if (shouldSet) setEffectiveFieldValues([...updatedFieldValues, ...addedFields])
  }, [filteredFieldValues, effectiveFieldValues])

  const fieldValues = effectiveFieldValues.map((fieldValue, index) => {
    if (!fieldValue || !fieldValue.field || !fieldValue.field.id) return null
    const isLastItem = !hasPendingFields && index === effectiveFieldValues.length - 1
    if (fieldValue.field === undefined || fieldValue.field.dataType === undefined) return null

    return getIssueFieldEditor({
      errorPresent,
      setErrorPresent: () => setErrorPresent(false),
      fieldValue,
      readonly: !data.viewerCanSetFields,
      hideDivider: isLastItem,
      onCommit: (fieldId: string, value: IssueFieldSelectionValues) =>
        onCommitField(fieldId, fieldValue.field.dataType!, value),
    })
  })

  return <>{fieldValues}</>
}

try{ CreateIssueFieldsSection.displayName ||= 'CreateIssueFieldsSection' } catch {}
try{ EditIssueFieldsSection.displayName ||= 'EditIssueFieldsSection' } catch {}
try{ IssueFields.displayName ||= 'IssueFields' } catch {}